import React from 'react';

import { Box, Typography } from '@mui/material';

import { Field, useField, useForm } from '@hrlogics/react-components/form';

import { ClearVerifyStaffRole } from 'src/api';
import { CheckboxField, RadioGroupField, RadioGroupOption } from 'src/components/finalForm';
import { CLEAR_VERIFY_STAFF_ROLE_OPTIONS } from 'src/constants/clearVerifyStaff';
import type { SxProp } from 'src/types/mui';

import { AccessFormField } from './constants';

interface ClearVerifyStaffAccessFormPartProps {
  disabled?: boolean;
}

/** Role and financial-report access of a Clear Verify staff member. A Manager has no financial access. */
export const ClearVerifyStaffAccessFormPart: React.FC<ClearVerifyStaffAccessFormPartProps> = ({
  disabled,
}) => {
  const form = useForm();
  const { input: role } = useField<ClearVerifyStaffRole>(AccessFormField.role, {
    subscription: { value: true },
  });
  const { input: financialReports } = useField<boolean>(
    AccessFormField.isFinancialReportAvailable,
    { subscription: { value: true } },
  );

  // The monthly report is available only with Financial reports, so turning that off clears it.
  const financialReportsInput = {
    ...financialReports,
    onChange: (checked: boolean) => {
      financialReports.onChange(checked);
      if (!checked) form.change(AccessFormField.isRecipientForMonthlyFinancialReport, false);
    },
  };

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Field
        label="Role"
        name={AccessFormField.role}
        component={RadioGroupField}
        options={roleOptions}
        optionControlLabelProps={{ sx: alignTopStyles }}
        disabled={disabled}
        fullWidth
      />

      {role.value !== ClearVerifyStaffRole.Manager && (
        <React.Fragment>
          <CheckboxField
            input={financialReportsInput}
            label={
              <OptionLabel
                title="Financial reports"
                description="Can generate the year-to-date financial report from the dashboard."
              />
            }
            formControlLabelProps={{ disabled, sx: alignTopStyles }}
          />
          <Field
            name={AccessFormField.isRecipientForMonthlyFinancialReport}
            component={CheckboxField}
            label={
              <OptionLabel
                title="Monthly report"
                description="Receives that report by email each month while their account is active."
              />
            }
            formControlLabelProps={{
              disabled: disabled || !financialReports.value,
              sx: alignTopStyles,
            }}
          />
        </React.Fragment>
      )}
    </Box>
  );
};

const OptionLabel: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <Box pt={1}>
    <Typography fontWeight={500}>{title}</Typography>
    <Typography variant="body2" color="text.secondary">
      {description}
    </Typography>
  </Box>
);

const roleOptions: RadioGroupOption[] = CLEAR_VERIFY_STAFF_ROLE_OPTIONS.map(
  ({ value, title, description }) => ({
    value,
    label: <OptionLabel title={title} description={description} />,
  }),
);

const alignTopStyles: SxProp = { alignItems: 'flex-start' };
